import type { ValueOf } from 'type-fest';

import {
  ANNOTATION_BORDER_WIDTH,
  ANNOTATION_COLOR_CODES,
  ANNOTATION_OPACITY,
  SUGGESTION_ANNOTATION_COMMAND_ORIGINS,
  SUGGESTION_ANNOTATION_TYPES,
} from 'modules/common/viewer/pdfViewer/annotationCommands/consts';
import {
  type FoundInFileMetadata,
  type SuggestionAnnotationRaw,
  ANNOTATION_METADATA_TYPES,
} from 'modules/common/viewer/pdfViewer/annotationCommands/types';

// ---------------------------------------------------------------------------
// Display state
// ---------------------------------------------------------------------------

export const ANNOTATION_DISPLAY_STATES = {
  DEFAULT: 'default',
  DIMMED: 'dimmed',
  HIGHLIGHTED: 'highlighted',
} as const;

export type AnnotationDisplayState =
  (typeof ANNOTATION_DISPLAY_STATES)[keyof typeof ANNOTATION_DISPLAY_STATES];

export type AnnotationDisplayStateResult = {
  state: AnnotationDisplayState;
  isBleedingOrange: boolean;
};

export const DEFAULT_DISPLAY_STATE: AnnotationDisplayStateResult = {
  state: ANNOTATION_DISPLAY_STATES.DEFAULT,
  isBleedingOrange: false,
};

export const DIMMED_DISPLAY_STATE: AnnotationDisplayStateResult = {
  state: ANNOTATION_DISPLAY_STATES.DIMMED,
  isBleedingOrange: false,
};

// ---------------------------------------------------------------------------
// Mode config contract
// ---------------------------------------------------------------------------

/**
 * Contextual information available to mode config callbacks.
 *
 * Each viewer populates only the fields it has; absent fields default to
 * empty sets so config callbacks can safely call `.has()` on any field.
 */
export type AnnotationModeContext = {
  overlappedTextLayerAnnotationIds: ReadonlySet<string>;
  partbotExtractionIdsConflictingWithFieldSuggestions: ReadonlySet<string>;
  /**
   * Field suggestion annotation IDs that share an identical loc with a
   * Found-in-Files annotation. Used to suppress the fieldSuggestion overlay
   * on tabs where the richer FiF annotation should be the sole clickable target.
   */
  fieldSuggestionIdsConflictingWithPartBotExtractions: ReadonlySet<string>;
  activeActionExtractionAnnotIds: ReadonlySet<string>;
  activeTextExtractionAnnotIds: ReadonlySet<string>;
  bleedingOrangeAnnotIds: ReadonlySet<string>;
  bleedingOrangeTextExtractionAnnotIds: ReadonlySet<string>;
  whiteoutCategories: ReadonlySet<string>;
};

/** Returns an AnnotationModeContext with every field set to an empty Set. */
export const EMPTY_MODE_CONTEXT: AnnotationModeContext = {
  overlappedTextLayerAnnotationIds: new Set(),
  partbotExtractionIdsConflictingWithFieldSuggestions: new Set(),
  fieldSuggestionIdsConflictingWithPartBotExtractions: new Set(),
  activeActionExtractionAnnotIds: new Set(),
  activeTextExtractionAnnotIds: new Set(),
  bleedingOrangeAnnotIds: new Set(),
  bleedingOrangeTextExtractionAnnotIds: new Set(),
  whiteoutCategories: new Set(),
};

export type AnnotationModeConfig = {
  isVisible: (
    annot: SuggestionAnnotationRaw,
    ctx: AnnotationModeContext,
  ) => boolean;
  whiteoutEnabled: boolean;
  getDisplayState: (
    annot: SuggestionAnnotationRaw,
    ctx: AnnotationModeContext,
  ) => AnnotationDisplayStateResult;
};

// ---------------------------------------------------------------------------
// Visual property helpers
// ---------------------------------------------------------------------------

const getVisualPropertiesFromState = (
  state: AnnotationDisplayState,
  annot: SuggestionAnnotationRaw,
  isBleedingOrange: boolean,
) => {
  const colorCode = isBleedingOrange
    ? ANNOTATION_COLOR_CODES.BLEEDING_ORANGE
    : state === ANNOTATION_DISPLAY_STATES.HIGHLIGHTED
    ? ANNOTATION_COLOR_CODES.REQS_REVIEW
    : annot.colorCode;
  const opacity =
    state === ANNOTATION_DISPLAY_STATES.DIMMED
      ? ANNOTATION_OPACITY.DEPRIORITIZED
      : annot.opacity;
  const borderWidth =
    state === ANNOTATION_DISPLAY_STATES.DIMMED
      ? ANNOTATION_BORDER_WIDTH.DIMMED
      : annot.borderWidth;
  return { colorCode, opacity, borderWidth, isBleedingOrange };
};

export const getVisualPropertiesFromDisplayState = (
  result: AnnotationDisplayStateResult,
  annot: SuggestionAnnotationRaw,
) => getVisualPropertiesFromState(result.state, annot, result.isBleedingOrange);

// ---------------------------------------------------------------------------
// Config factory helper
// ---------------------------------------------------------------------------

export const createDefaultConfig = (
  whiteoutEnabled: boolean,
  getDisplayState: AnnotationModeConfig['getDisplayState'],
): AnnotationModeConfig => ({
  isVisible: () => true,
  whiteoutEnabled,
  getDisplayState,
});

// ---------------------------------------------------------------------------
// Other helpers
// ---------------------------------------------------------------------------

export const isVisibleFiFAndTablesOnly = (annot: SuggestionAnnotationRaw) =>
  annot.type === SUGGESTION_ANNOTATION_TYPES.FOUND_IN_FILES ||
  annot.type === SUGGESTION_ANNOTATION_TYPES.EXTRACTED_TABLE;

/** Viewer state used to decide whether raw-region FiF overlays stay hidden. */
export type AnnotationGlobalVisibilityContext = {
  isFocused: boolean;
  focusOrigin: ValueOf<typeof SUGGESTION_ANNOTATION_COMMAND_ORIGINS> | null;
};

/**
 * Raw-region Found-in-Files annotations are hidden unless the user opened the
 * finding from the sidebar pill (PILL focus), so the PDF is not cluttered until
 * they ask to see the region.
 */
export const hideIfUnfocuedRawRegion = (
  annot: SuggestionAnnotationRaw,
  visibility: AnnotationGlobalVisibilityContext,
): boolean => {
  if (annot.metadata?.type === ANNOTATION_METADATA_TYPES.FOUND_IN_FILES) {
    const fifMetadata = annot.metadata as FoundInFileMetadata;
    if (fifMetadata.partBotResult?.rawRegion) {
      const isPillFocused =
        visibility.isFocused &&
        visibility.focusOrigin === SUGGESTION_ANNOTATION_COMMAND_ORIGINS.PILL;
      return !isPillFocused;
    }
  }
  return false;
};
